/**
 * SuggestEditForm — page-level BottomSheet for suggesting edits on a
 * provider_organization or provider_individual.
 *
 * X.5.3 (multi-field redesign): the form now collects MULTIPLE
 * field -> value rows in one submission (was a single-field picker). On
 * submit it creates one suggested_edits row per field via N
 * `suggestedEditsApi.submit` calls (the steward queue reviews per-row, so
 * no review-side change). The backend has no batch endpoint; validation is
 * front-loaded so a bad row submits nothing, and a partial network failure
 * is surfaced honestly (never a silent partial-submit).
 *
 * Shape:
 *   - SuggestChangeButton (peace-hub-port) opens the sheet via `isOpen`.
 *   - Each row: a field <select> (allowlisted fields per
 *     src/lib/suggestedEdits.ts ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE; a field
 *     already picked is disabled in the other rows) + a current-value hint
 *     + a new-value <input>. "+ Add another field" appends a row; the ✕
 *     button removes one.
 *   - One shared rationale textarea applies to every row.
 *   - "Remove from directory" (is_active per §3.6) is an inline archive
 *     request: no value input, shared rationale REQUIRED, submitted as
 *     field_name=is_active / new_value='false' (unchanged wire shape).
 *
 * UX sensibility (Amanda 2026-05-09 + design plan §3.7; X.5.3 relabel):
 *   - "Suggest a change" headline (always; no per-field takeover).
 *   - "What needs updating?" picker label, "Currently: ..." old value,
 *     "What should it be?" new value, "Anything we should know? (optional)"
 *     rationale (becomes "Why are you suggesting this?" when an archive row
 *     is present).
 *   - "Send for review" submit button (CM-friendly; was "Send to steward").
 *
 * Per-field phone/email validators (Phase 1.7.2 A4) run per row; the server
 * stays the security boundary + value-coercion gate (ADR-021 / 6.F.4).
 */

import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { X, Plus, Send } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE,
  suggestedEditsApi,
} from '@/lib/suggestedEdits';
import { lookupsApi } from '@/lib/lookups';
import { invalidateAdminReviewKeys } from '@/lib/adminReview';
import type {
  IndividualDetail,
  ProviderDetail,
  SuggestedEditTargetType,
} from '@/lib/types';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { ControlledMultiSelect } from '@/components/ControlledMultiSelect';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import type { NameCaseKind } from '@/lib/valueCase';
import { GENDER_RESTRICTION_VALUES, MEDICAID_VALUES, SERVICE_DELIVERY_MODELS } from '@/lib/facetTaxonomy';
import {
  FLD,
  FLD_TEXTAREA,
  FLD_LABEL,
  BTN_GHOST,
  BTN_GOLD,
  MODAL_FOOT,
  ModalCloseButton,
} from '@/components/primitives/modalFormStyles';
import { emailValidator, phoneValidator } from '@/lib/validators';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { SelectField } from '@/components/primitives/SelectField';
import { AddressCheckChoices, AddressCheckPrompt, AddressFields, useAddressForm, type AddressFormParts } from '@/components/clientRecord/AddressesCard';
import { formatPostalParts } from '@/lib/address';
import { refreshFamily } from '@/lib/refreshFamily';

// Phase 1.7.2 — phone/email validators wired at the suggest-edit
// boundary per opener's A4 item. Map allowlist field names to the
// validator they should run. Fields not in the map skip validation.
const FIELD_VALIDATORS: Record<string, (v: string) => string | null> = {
  phone: phoneValidator,
  business_phone: phoneValidator,
  cell_phone: phoneValidator,
  // A fax is checked as a phone, on an organization and on a person.
  fax: phoneValidator,
  email: emailValidator,
};

// Friendly field-label map per design plan §3.7. is_active surfaces as
// "Remove from directory" — names the user-facing action, not the
// schema column. The mid-form context shift triggers when the user
// picks this option (per design plan §3.6 suggest-archive flow).
//
// Phase 1.9.1 hotfix (2026-05-13): A5 cohort labels added. The server
// allowlist + UI allowlist mirror (src/lib/suggestedEdits.ts:92-97)
// have included these fields since Phase 1.7 (migration 0058 ship),
// but ORG_FIELD_LABELS wasn't updated in lockstep — the field-picker
// dropdown fell back to raw column names ('medicaid', 'dementia_unit')
// which users couldn't recognize. Phase 1.9.1's Features filter made
// the gap user-visible: CMs filtering by Medicaid + Memory Care had
// no way to ADD values to providers, because the suggest-edit picker's
// labels for those fields were the raw column names.
const ORG_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  address: 'Address',
  website: 'Website',
  // Phase 1.7.2 (migration 0060): `notes` removed from suggest-edit
  // options. Legacy column dropped; notes-write flow via CmWriteNoteForm
  // BottomSheet (versioned provider_notes append).
  is_active: 'Remove from directory',
  // Phase 1.7 A5 cohort (migration 0058) — labels added at 1.9.1
  // hotfix. Server's coerceSuggestedEditValue handles each shape:
  //   - medicaid: string pass-through (user types e.g. "Yes · Straight")
  //   - dementia_unit: 'true' / 'false' / null coerced to boolean
  //   - payment_types: JSON-stringified string[] (or null to clear)
  //   - phone_2 / fax / service_delivery_model: string pass-through
  medicaid: 'Medicaid acceptance',
  dementia_unit: 'Secure Dementia Unit',
  phone_2: 'Secondary phone',
  fax: 'Fax',
  payment_types: 'Payment types',
  service_delivery_model: 'Delivery method',
  // Phase 1.9.3 — added with migration 0068 backfill.
  gender_restrictions: 'Gender restrictions',
  // Item 5 — M:N junction fields. Rendered with a multiselect (not the
  // text input) and submitted as a `{ add, remove }` slug delta.
  subcategories: 'Subcategories',
  specialties: 'Specialties',
};

// Item 5 — org fields backed by M:N junctions (provider_subcategorizations
// / provider_specializations). These render a multiselect seeded with the
// org's current tags and submit a slug DELTA, not a scalar value.
const ORG_JUNCTION_FIELD_NAMES = new Set<string>(['subcategories', 'specialties']);

const INDIVIDUAL_FIELD_LABELS: Record<string, string> = {
  full_name: 'Full name',
  title: 'Title',
  business_phone: 'Business phone',
  cell_phone: 'Cell phone',
  fax: 'Fax',
  email: 'Email',
  address: 'Address',
  specialties: 'Specialties',
  notes: 'Notes',
  is_active: 'Remove from directory',
};

/** The name kind of a suggestable field: an organization's name is a
 *  place, a person's full name a person, a title a role. Every other
 *  field (an address, a phone, a note, a choice) is kept as typed. */
function nameKindFor(targetType: SuggestedEditTargetType, fieldName: string): NameCaseKind | undefined {
  if (targetType === 'organization' && fieldName === 'name') return 'place';
  if (targetType === 'individual' && fieldName === 'full_name') return 'person';
  if (fieldName === 'title') return 'role';
  return undefined;
}

/** One submit's variables, read at the press. */
interface SubmitVars {
  payloads: SubmitPayload[];
  /** A junction field's seeded slugs, by field name, for rebuilding a failed row. */
  seeds: Record<string, string[]>;
  family: readonly unknown[] | null;
}

/** The option fields: a typed value that matches an option ignoring case
 *  takes that option's spelling on leaving the box ("no" reads "No"). */
const OPTION_VALUES: Readonly<Record<string, readonly string[]>> = {
  medicaid: MEDICAID_VALUES,
  gender_restrictions: GENDER_RESTRICTION_VALUES,
  service_delivery_model: SERVICE_DELIVERY_MODELS,
};

function optionSpelling(fieldName: string, typed: string): string | null {
  const options = OPTION_VALUES[fieldName];
  if (options === undefined) return null;
  const wanted = typed.trim().toLowerCase();
  return options.find((o) => o.toLowerCase() === wanted) ?? null;
}

export interface SuggestEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  tenantSlug: string;
  targetType: SuggestedEditTargetType;
  /** Either a ProviderDetail or IndividualDetail; used to render the
   *  current value of the picked field for the user's reference. */
  target: ProviderDetail | IndividualDetail;
  /** Cache key for invalidation when a suggestion submits. */
  invalidationKey?: ReadonlyArray<unknown>;
}

// One field -> new-value row in the multi-field form. `id` is a stable
// React key (counter-based) so removing a middle row doesn't reshuffle
// the controlled inputs.
interface EditRow {
  id: number;
  fieldName: string;
  newValue: string;
  /**
   * Item 5 — selected slugs for a junction field (subcategories /
   * specialties). Undefined for scalar fields. Initialized to the org's
   * current slugs when a junction field is picked; the submit delta is
   * computed against that seed.
   */
  selectedSlugs?: string[];
}

// Wire payload for a single suggested_edits create.
interface SubmitPayload {
  target_type: SuggestedEditTargetType;
  target_id: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  rationale: string | null;
}

export function SuggestEditForm({
  isOpen,
  onClose,
  tenantSlug,
  targetType,
  target,
  invalidationKey,
}: SuggestEditFormProps): ReactNode {
  const queryClient = useQueryClient();
  const nextRowId = useRef(2);
  const [rows, setRows] = useState<EditRow[]>([{ id: 1, fieldName: '', newValue: '' }]);
  const [rationale, setRationale] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  // A refused submit keeps its error and is worded at render. The support
  // contact is read only once a refusal is showing, so a form that sends
  // cleanly makes no request beyond its own.
  const [refusal, setRefusal] = useState<unknown>(null);
  const contact = useSupportContact({ enabled: refusal !== null });
  const error = refusal !== null ? suggestionRefusalText(refusal, contact) : errorText;
  function setError(text: string | null): void {
    setErrorText(text);
    setRefusal(null);
  }

  const labels = targetType === 'organization' ? ORG_FIELD_LABELS : INDIVIDUAL_FIELD_LABELS;
  const allowedFields = ALLOWED_SUGGESTABLE_FIELDS_BY_TYPE[targetType];

  // Item 5 — junction-field plumbing (org Subcategories / Specialties).
  const isJunctionField = (fieldName: string): boolean =>
    targetType === 'organization' && ORG_JUNCTION_FIELD_NAMES.has(fieldName);

  // The org's CURRENT slugs/labels for a junction field (the submit seed).
  // Empty for non-org targets or non-junction fields.
  function seededRefsFor(
    fieldName: string,
  ): Array<{ slug: string; display_name: string }> {
    if (targetType !== 'organization') return [];
    const p = target as ProviderDetail;
    if (fieldName === 'subcategories') return p.subcategories;
    if (fieldName === 'specialties') return p.specialties;
    return [];
  }
  const seededSlugsFor = (fieldName: string): string[] =>
    seededRefsFor(fieldName).map((r) => r.slug);

  // Lookup vocab for the multiselect options. Only fetched once a junction
  // field is actually picked (not on every open) — cached + slug-keyed.
  const anyJunctionChosen = rows.some((r) => isJunctionField(r.fieldName));
  const subcategoryQuery = useQuery({
    queryKey: ['lookups', 'subcategories', 'all', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSubcategories(tenantSlug, { limit: 500 }, signal),
    enabled: isOpen && anyJunctionChosen,
    staleTime: 5 * 60_000,
  });
  const specialtyQuery = useQuery({
    queryKey: ['lookups', 'specialties', 'all', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    enabled: isOpen && anyJunctionChosen,
    staleTime: 5 * 60_000,
  });

  function optionsFor(fieldName: string): Array<{ value: string; label: string }> {
    const rows =
      fieldName === 'subcategories'
        ? subcategoryQuery.data?.rows
        : fieldName === 'specialties'
          ? specialtyQuery.data?.rows
          : undefined;
    return (rows ?? []).map((r) => ({ value: r.slug, label: r.display_name }));
  }

  // add = selected − seeded; remove = seeded − selected.
  function junctionDeltaFor(row: EditRow): { add: string[]; remove: string[] } {
    const seeded = seededSlugsFor(row.fieldName);
    const selected = row.selectedSlugs ?? seeded;
    return {
      add: selected.filter((s) => !seeded.includes(s)),
      remove: seeded.filter((s) => !selected.includes(s)),
    };
  }

  // Fields already chosen in some row — used to disable duplicate picks
  // in the other rows' selects.
  const chosenFields = useMemo(
    () => new Set(rows.map((r) => r.fieldName).filter((f) => f !== '')),
    [rows],
  );
  const hasArchiveRow = rows.some((r) => r.fieldName === 'is_active');
  const anyFieldChosen = rows.some((r) => r.fieldName !== '');

  // Current value of a field for the "Currently: ..." hint + old_value.
  // is_active reads as 'active' / 'archived' (the existing wire shape the
  // server expects for the suggest-archive flow).
  function currentValueFor(fieldName: string): string | null {
    if (!fieldName) return null;
    if (fieldName === 'is_active') {
      return (target as { is_active: boolean }).is_active ? 'active' : 'archived';
    }
    const v = (target as unknown as Record<string, unknown>)[fieldName];
    if (v === null || v === undefined || v === '') return null;
    return String(v);
  }

  function resetState(): void {
    nextRowId.current = 2;
    setRows([{ id: 1, fieldName: '', newValue: '' }]);
    setRationale('');
    setError(null);
  }

  /** Send the payloads with what a failed row's rebuild needs, read now. */
  function submit(payloads: SubmitPayload[]): void {
    const seeds: Record<string, string[]> = {};
    for (const p of payloads) {
      if (isJunctionField(p.field_name)) seeds[p.field_name] = seededSlugsFor(p.field_name);
    }
    mutation.mutate({ payloads, seeds, family: invalidationKey ? refreshFamily(invalidationKey) : null });
  }

  function handleClose(): void {
    // BottomSheet unmounts its children on close, but SuggestEditForm
    // stays mounted (SuggestChangeButton owns isOpen). handleClose IS
    // BottomSheet's onClose, so backdrop / ESC reset too.
    resetState();
    onClose();
  }

  function updateRow(id: number, patch: Partial<EditRow>): void {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setError(null);
  }

  function addRow(): void {
    setRows((prev) => [...prev, { id: nextRowId.current++, fieldName: '', newValue: '' }]);
    setError(null);
  }

  function removeRow(id: number): void {
    // Always keep at least one row.
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
    setError(null);
  }

  // Build one wire payload per field-chosen row. is_active -> 'false'
  // (archive request); the shared rationale applies to every row.
  function buildPayloads(activeRows: EditRow[], addressParts: AddressFormParts | null = null): SubmitPayload[] {
    const sharedRationale = rationale.trim().length === 0 ? null : rationale.trim();
    return activeRows.map((r) => {
      // A Directory address travels as its parts' JSON.
      if (isOrgAddress(r.fieldName) && addressParts !== null) {
        return {
          target_type: targetType,
          target_id: target.id,
          field_name: r.fieldName,
          old_value: currentValueFor(r.fieldName),
          new_value: JSON.stringify(addressParts),
          rationale: sharedRationale,
        };
      }
      // Item 5 — junction field: new_value is the `{ add, remove }` slug
      // delta; old_value is the seeded current-slugs snapshot (steward
      // context only — the server reconciles against live state at apply).
      if (isJunctionField(r.fieldName)) {
        return {
          target_type: targetType,
          target_id: target.id,
          field_name: r.fieldName,
          old_value: JSON.stringify(seededSlugsFor(r.fieldName)),
          new_value: JSON.stringify(junctionDeltaFor(r)),
          rationale: sharedRationale,
        };
      }
      const isArchive = r.fieldName === 'is_active';
      const newValue = isArchive ? 'false' : r.newValue.length === 0 ? null : r.newValue;
      return {
        target_type: targetType,
        target_id: target.id,
        field_name: r.fieldName,
        old_value: currentValueFor(r.fieldName),
        new_value: newValue,
        rationale: sharedRationale,
      };
    });
  }

  // The payloads, each junction field's seeded slugs (to rebuild a failed
  // row) and the list to refresh (the family) are read at the press and
  // travel as the variables (submit, below).
  const mutation = useMutation({
    mutationFn: async (v: SubmitVars) => {
      // No server batch endpoint — fire N creates. allSettled so one
      // failure doesn't hide the rest; the caller reports per-row outcome.
      const settled = await Promise.allSettled(
        v.payloads.map((p) => suggestedEditsApi.submit(tenantSlug, p)),
      );
      return v.payloads.map((p, i) => {
        const s = settled[i];
        return s.status === 'fulfilled'
          ? { payload: p, ok: true as const, error: null }
          : { payload: p, ok: false as const, error: s.reason as unknown };
      });
    },
    onSuccess: async (results, v) => {
      const failed = results.filter((r) => !r.ok);
      // Anything that DID land must refresh the queue + badge counts.
      if (failed.length < results.length) {
        if (v.family !== null) {
          await queryClient.invalidateQueries({ queryKey: v.family });
        }
        await invalidateAdminReviewKeys(queryClient);
      }
      if (failed.length === 0) {
        handleClose();
        return;
      }
      // Partial or total failure — keep ONLY the failed rows so a retry
      // re-sends exactly those (never silently partial-submitting; never
      // double-submitting the ones that already landed).
      nextRowId.current = failed.length + 1;
      setRows(
        failed.map((f, i) => {
          const fn = f.payload.field_name;
          // Item 5 — rebuild the junction selection from seed + delta so a
          // retry re-sends the same intent (the row stores selectedSlugs,
          // not the delta JSON).
          const seeded = v.seeds[fn];
          if (seeded !== undefined) {
            let delta: { add: string[]; remove: string[] } = { add: [], remove: [] };
            try {
              const p = JSON.parse(f.payload.new_value ?? '{}') as {
                add?: unknown;
                remove?: unknown;
              };
              if (Array.isArray(p.add) && Array.isArray(p.remove)) {
                delta = {
                  add: p.add.filter((s): s is string => typeof s === 'string'),
                  remove: p.remove.filter((s): s is string => typeof s === 'string'),
                };
              }
            } catch {
              // keep empty delta on parse failure
            }
            const selected = [...new Set([...seeded, ...delta.add])].filter(
              (s) => !delta.remove.includes(s),
            );
            return { id: i + 1, fieldName: fn, newValue: '', selectedSlugs: selected };
          }
          return {
            id: i + 1,
            fieldName: fn,
            newValue: fn === 'is_active' ? '' : (f.payload.new_value ?? ''),
          };
        }),
      );
      if (failed.length === results.length) {
        setErrorText(null);
        setRefusal(failed[0].error);
      } else {
        setError(
          `Sent ${results.length - failed.length} of ${results.length}. The rest didn't send, so press Send for review again.`,
        );
      }
    },
  });

  // A Directory listing's address row (an organization's or a person's)
  // is the shared address form (a Directory address: a town and a state
  // at least); a street is checked first.
  const isOrgAddress = (fieldName: string): boolean => fieldName === 'address';
  const orgTarget = target as { address_line1?: string | null; address_line2?: string | null; address_city?: string | null; address_state?: string | null; address_zip?: string | null };
  const storedAddress: AddressFormParts | null =
    (orgTarget.address_line1 ?? null) !== null || (orgTarget.address_city ?? null) !== null
      ? {
          line1: orgTarget.address_line1 ?? null,
          line2: orgTarget.address_line2 ?? null,
          city: orgTarget.address_city ?? null,
          state: orgTarget.address_state ?? null,
          zip: orgTarget.address_zip ?? null,
        }
      : null;
  // The address row's form registers its Save here while it is shown.
  const pressAddressSave = useRef<(() => void) | null>(null);
  function sendWithAddress(parts: AddressFormParts): void {
    if (formatPostalParts(parts) === (currentValueFor('address') ?? '')) {
      setError(`${labels['address'] ?? 'Address'}: that's the same as what's there now. Change it, or remove that field.`);
      return;
    }
    submit(buildPayloads(rows.filter((r) => r.fieldName !== ''), parts));
  }

  function handleSubmit(): void {
    setError(null);
    const activeRows = rows.filter((r) => r.fieldName !== '');
    if (activeRows.length === 0) {
      setError('Pick at least one field to change.');
      return;
    }
    // Defensive duplicate guard (the UI also disables taken options).
    const seen = new Set<string>();
    for (const r of activeRows) {
      if (seen.has(r.fieldName)) {
        setError('Each field can only be suggested once. Remove the repeated one, then send.');
        return;
      }
      seen.add(r.fieldName);
    }
    // Archive rows require the shared rationale (§3.6).
    if (hasArchiveRow && rationale.trim().length === 0) {
      setError('Say why this should be removed.');
      return;
    }
    // Per-row validation up front (all-or-nothing gate): vacuous guard +
    // phone/email format. A failure here submits nothing.
    for (const r of activeRows) {
      if (r.fieldName === 'is_active') continue;
      // Item 5 — junction rows are vacuous when the delta is empty (no
      // tag added and none removed), not when newValue===current.
      if (isJunctionField(r.fieldName)) {
        const d = junctionDeltaFor(r);
        if (d.add.length === 0 && d.remove.length === 0) {
          setError(
            `${labels[r.fieldName] ?? r.fieldName}: add or remove at least one to suggest a change.`,
          );
          return;
        }
        continue;
      }
      if (isOrgAddress(r.fieldName)) continue;
      const current = currentValueFor(r.fieldName) ?? '';
      if (r.newValue === current) {
        setError(
          `${labels[r.fieldName] ?? r.fieldName}: that's the same as what's there now. Change it, or remove that field.`,
        );
        return;
      }
      const validator = FIELD_VALIDATORS[r.fieldName];
      if (validator) {
        const problem = validator(r.newValue);
        if (problem !== null) {
          setError(`${labels[r.fieldName] ?? r.fieldName}: ${problem}`);
          return;
        }
      }
    }
    if (activeRows.some((r) => isOrgAddress(r.fieldName)) && pressAddressSave.current !== null) {
      pressAddressSave.current();
      return;
    }
    submit(buildPayloads(activeRows));
  }

  const rationaleLabel = hasArchiveRow
    ? 'Why are you suggesting this?'
    : 'Anything we should know? (optional)';

  // Prototype modal-head subtitle = the resource name (SuggestChange).
  const targetName =
    targetType === 'organization'
      ? (target as { name?: string }).name
      : (target as { full_name?: string }).full_name;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Suggest a change"
      subtitle={targetName ?? 'Tell us what to update.'}
      headerRight={<ModalCloseButton onClick={handleClose} />}
      centered
    >
      <div className="px-[26px] py-[22px] space-y-4">
        {rows.map((row, idx) => {
          const isArchive = row.fieldName === 'is_active';
          const isJunction = isJunctionField(row.fieldName);
          const current = currentValueFor(row.fieldName);
          return (
            <div
              key={row.id}
              className="space-y-2 border-b border-sage-800/[8%] pb-4 last:border-b-0 last:pb-0"
            >
              <div className="flex items-start gap-2">
                <label className="block flex-1">
                  <span className={FLD_LABEL}>What needs updating?</span>
                  <SelectField size="field"
                    value={row.fieldName}
                    onChange={(e) => {
                      const fn = e.target.value;
                      updateRow(row.id, {
                        fieldName: fn,
                        newValue: '',
                        // Seed junction selection with the org's current
                        // slugs; clear it for scalar fields.
                        selectedSlugs: isJunctionField(fn)
                          ? seededSlugsFor(fn)
                          : undefined,
                      });
                    }}
                    disabled={mutation.isPending}
                    aria-label={`Field to change (row ${idx + 1})`}
                    className="w-full"
                  >
                    <option value="">Pick a field…</option>
                    {allowedFields.map((f) => (
                      <option
                        key={f}
                        value={f}
                        disabled={chosenFields.has(f) && f !== row.fieldName}
                      >
                        {labels[f] ?? f}
                      </option>
                    ))}
                  </SelectField>
                </label>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    disabled={mutation.isPending}
                    aria-label={`Remove ${row.fieldName ? (labels[row.fieldName] ?? row.fieldName) : `row ${idx + 1}`}`}
                    className="mt-[26px] w-[38px] h-[46px] shrink-0 inline-flex items-center justify-center text-sage-400 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-xl"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>

              {isOrgAddress(row.fieldName) && (
                <>
                  <p className="text-xs text-ink-soft">
                    Currently:{' '}
                    <span className="font-mono text-ink">{current ?? '(empty)'}</span>
                  </p>
                  <OrgAddressFields
                    existing={storedAddress}
                    busy={mutation.isPending}
                    pressRef={pressAddressSave}
                    onSave={sendWithAddress}
                  />
                </>
              )}

              {row.fieldName !== '' && !isArchive && !isJunction && !isOrgAddress(row.fieldName) && (
                <>
                  <p className="text-xs text-ink-soft">
                    Currently:{' '}
                    <span className="font-mono text-ink">{current ?? '(empty)'}</span>
                  </p>
                  <label className="block">
                    <span className={FLD_LABEL}>What should it be?</span>
                    <TextInput
                      type="text"
                      nameCase={nameKindFor(targetType, row.fieldName)}
                      value={row.newValue}
                      onChange={(e) => updateRow(row.id, { newValue: e.target.value })}
                      onBlur={() => {
                        const spelled = optionSpelling(row.fieldName, row.newValue);
                        if (spelled !== null && spelled !== row.newValue) updateRow(row.id, { newValue: spelled });
                      }}
                      placeholder={`New value for ${(labels[row.fieldName] ?? row.fieldName).toLowerCase()}`}
                      aria-label={`New value for ${labels[row.fieldName] ?? row.fieldName}`}
                      disabled={mutation.isPending}
                      className={FLD}
                    />
                  </label>
                </>
              )}

              {row.fieldName !== '' && isJunction && (
                <>
                  <p className="text-xs text-ink-soft">
                    Currently:{' '}
                    <span className="text-ink">
                      {seededRefsFor(row.fieldName).length === 0
                        ? '(none)'
                        : seededRefsFor(row.fieldName)
                            .map((r) => r.display_name)
                            .join(', ')}
                    </span>
                  </p>
                  <div className="block">
                    <span className={FLD_LABEL}>What should it be?</span>
                    <div className="mt-1">
                      <ControlledMultiSelect
                        value={row.selectedSlugs ?? seededSlugsFor(row.fieldName)}
                        options={optionsFor(row.fieldName)}
                        onChange={(next) =>
                          updateRow(row.id, { selectedSlugs: next })
                        }
                        label={
                          row.fieldName === 'subcategories'
                            ? 'subcategory'
                            : 'specialty'
                        }
                        disabled={mutation.isPending}
                      />
                    </div>
                  </div>
                </>
              )}

              {isArchive && (
                <p className="text-[13px] text-[#A33A2D] bg-[#FAEAE8] rounded-[10px] px-[13px] py-[9px] leading-[1.45]">
                  This asks a data steward to remove the resource from the directory. It stays
                  visible until they approve.
                </p>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={addRow}
          disabled={mutation.isPending || rows.length >= allowedFields.length}
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-sage-400 hover:text-sage-800 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-md"
        >
          <Plus size={15} aria-hidden="true" />
          Add another field
        </button>

        {anyFieldChosen && (
          <label className="block">
            <span className={FLD_LABEL}>{rationaleLabel}</span>
            <TextArea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={3}
              aria-label="Rationale"
              disabled={mutation.isPending}
              className={FLD_TEXTAREA}
            />
          </label>
        )}

        {error !== null && (
          <div
            role="alert"
            className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-xl px-3 py-2"
            data-testid="suggest-edit-error"
          >
            {error}
          </div>
        )}
      </div>

      <div className={MODAL_FOOT}>
        <button
          type="button"
          onClick={handleClose}
          disabled={mutation.isPending}
          className={BTN_GHOST}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={mutation.isPending || !anyFieldChosen}
          className={BTN_GOLD}
        >
          <Send size={15} aria-hidden="true" />
          {mutation.isPending ? 'Sending…' : 'Send for review'}
        </button>
      </div>
    </BottomSheet>
  );
}

/** An organization's address row: the shared address form (a Directory
 *  address), mounted only while the row is shown. Its Save runs from the
 *  sheet's own button through `pressRef`; a checked address goes to
 *  `onSave`. */
function OrgAddressFields({
  existing,
  busy,
  pressRef,
  onSave,
}: {
  existing: AddressFormParts | null;
  busy: boolean;
  pressRef: MutableRefObject<(() => void) | null>;
  onSave: (parts: AddressFormParts) => void;
}): ReactNode {
  const form = useAddressForm({ existing, rule: 'directory', idPrefix: 'suggest-edit', done: false, saving: busy, onSave });
  useEffect(() => {
    pressRef.current = form.pressSave;
    return () => {
      pressRef.current = null;
    };
  });
  return (
    <>
      <AddressFields idPrefix="suggest-edit" {...form.fields} />
      <AddressCheckPrompt phase={form.check.phase} />
      {form.prompting && (
        <div className="flex flex-wrap justify-end gap-2">
          <AddressCheckChoices
            phase={form.check.phase}
            busy={busy}
            onKeep={() => form.choose('checked', false)}
            onUse={() => form.choose('checked', true)}
            onSaveAsTyped={() => form.choose('saved_as_typed', false)}
          />
        </div>
      )}
    </>
  );
}
